import { useCallback } from 'react';
import { calculateRUTDigit, formatRUT } from '../utils/formatters';
import { validators } from '../utils/validators';
import userService from '../services/UserServices';
import { RegistrationFormData } from './useRegistrationForm';

// ✅ INTERFACE UNIFICADA PARA VALIDACIONES
export interface ValidationResult {
  isValid: boolean;
  error: string;
}

// ✅ INTERFACE PARA VALIDACIONES DEL FORMULARIO COMPLETO
export interface FormValidationResult {
  isValid: boolean;
  errors: Partial<Record<keyof RegistrationFormData, string>>;
  hasErrors: boolean;
}

export const useFormValidation = () => {
  const validateRUT = useCallback(
    async (rut: string): Promise<ValidationResult> => {
      if (!rut) {
        return { isValid: false, error: 'RUT es requerido' };
      }

      const formatValidation = validators.rut(rut);
      if (!formatValidation.isValid) {
        return { isValid: false, error: formatValidation.errorMessage };
      }

      const formattedValue = formatRUT(rut);
      const checkDigit = calculateRUTDigit(+formattedValue.slice(0, -2));
      if (
        checkDigit.toString() !==
        formattedValue.charAt(formattedValue.length - 1)
      ) {
        return { isValid: false, error: 'El RUT entregado no es válido' };
      }

      try {
        const response = await userService.isRegistered(formattedValue);
        console.log('response', response);
        if (response.user !== null) {
          return {
            isValid: false,
            error: 'El RUT ingresado ya está registrado',
          };
        }
      } catch (error) {
        return { isValid: false, error: 'Error al validar el RUT' };
      }

      return { isValid: true, error: '' };
    },
    []
  );

  const validatePassword = useCallback(
    (password: string, confirmation?: string): ValidationResult => {
      if (!password) {
        return { isValid: false, error: 'Contraseña es requerida' };
      }

      const strengthValidation = validators.password(password);
      if (!strengthValidation.isValid) {
        return { isValid: false, error: strengthValidation.errorMessage };
      }

      if (confirmation && password !== confirmation) {
        return { isValid: false, error: 'Las contraseñas no coinciden' };
      }

      return { isValid: true, error: '' };
    },
    []
  );

  const validatePhone = useCallback(
    async (phone: string): Promise<ValidationResult> => {
      if (!phone) {
        return { isValid: false, error: 'Teléfono es requerido' };
      }

      const formatValidation = validators.phone(phone);
      if (!formatValidation.isValid) {
        return { isValid: false, error: formatValidation.errorMessage };
      }

      try {
        const response = await userService.phoneIsRegistered(phone);
        if (response.user !== null) {
          return {
            isValid: false,
            error: 'El teléfono ingresado ya está registrado',
          };
        }
      } catch (error: any) {
        // Si el error es 404, el teléfono no está registrado, por lo tanto es válido
        if (error?.response?.status === 404) {
          return { isValid: true, error: '' };
        }
        // Para cualquier otro error, retornar error de validación
        return { isValid: false, error: 'Error al validar el teléfono' };
      }

      return { isValid: true, error: '' };
    },
    []
  );

  const validateRequiredField = useCallback(
    (value: string, fieldName: string): ValidationResult => {
      if (!value || value.trim() === '') {
        return { isValid: false, error: `${fieldName} es requerido` };
      }
      return { isValid: true, error: '' };
    },
    []
  );

  const validateNumericField = useCallback(
    (value: string, fieldName: string): ValidationResult => {
      if (!value) {
        return { isValid: false, error: `${fieldName} es requerido` };
      }
      const regex = /^[0-9\b]+$/;
      if (!regex.test(value)) {
        return {
          isValid: false,
          error: `${fieldName} debe contener solo números`,
        };
      }
      return { isValid: true, error: '' };
    },
    []
  );

  // ✅ VALIDACIÓN COMPLETA DEL FORMULARIO
  const validateForm = useCallback(
    async (formData: RegistrationFormData): Promise<FormValidationResult> => {
      const errors: Partial<Record<keyof RegistrationFormData, string>> = {};

      // Validar RUT
      const rutValidation = await validateRUT(formData.RUT);
      if (!rutValidation.isValid) {
        errors.RUTError = rutValidation.error;
      }

      // Validar campos requeridos
      const nameValidation = validateRequiredField(formData.name, 'Nombre');
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
      }

      const lastNameValidation = validateRequiredField(
        formData.lastName,
        'Apellido'
      );
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error;
      }

      // Validar contraseña
      const passwordValidation = validatePassword(
        formData.password,
        formData.confirmation
      );
      if (!passwordValidation.isValid) {
        errors.passwordError = passwordValidation.error;
      }

      // Validar confirmación
      if (
        formData.confirmation &&
        formData.password !== formData.confirmation
      ) {
        errors.passwordError = 'Las contraseñas no coinciden';
      }

      // Validar peso y altura
      const weightValidation = validateNumericField(formData.weight, 'Peso');
      if (!weightValidation.isValid) {
        errors.weight = weightValidation.error;
      }

      const heightValidation = validateNumericField(formData.height, 'Altura');
      if (!heightValidation.isValid) {
        errors.height = heightValidation.error;
      }

      // Validar teléfono
      const phoneValidation = await validatePhone(formData.phone);
      if (!phoneValidation.isValid) {
        errors.phoneError = phoneValidation.error;
      }

      // Validar campos de salud
      if (!formData.birthday) {
        errors.birthday = 'Fecha de nacimiento es requerida';
      }

      if (!formData.sex) {
        errors.sex = 'Sexo es requerido';
      }

      const hasErrors = Object.keys(errors).length > 0;
      const isValid = !hasErrors;

      return {
        isValid,
        errors,
        hasErrors,
      };
    },
    [
      validateRUT,
      validatePassword,
      validatePhone,
      validateRequiredField,
      validateNumericField,
    ]
  );

  return {
    validateRUT,
    validatePassword,
    validatePhone,
    validateRequiredField,
    validateNumericField,
    validateForm, // ✅ NUEVA FUNCIÓN UNIFICADA
  };
};
